import {
  MIN_CONTRAST,
  bookSeed,
  clamp,
  contrast,
  coverColour,
  hashString,
  hslToRgb,
  mostReadable,
  rgbCSS,
} from '../color.js';
import { groupByShelf, shelfLabel, shortTitle } from '../shelves.js';

// The classic shelf: the bookshelf a room has, drawn in wood and paper.

// Decade-appropriate spine typography, oldest to newest. Undated books (no
// book_published from Goodreads) and anything newer fall through to the
// current-era default at the end.
const DECADE_FONTS = [
  { before: 1960, family: "'Playfair Display', serif" }, // classic book typesetting
  { before: 1970, family: "'Special Elite', monospace" }, // 60s typewriter/zine
  { before: 1980, family: "'Alfa Slab One', serif" }, // 70s bold paperback slab
  { before: 1990, family: "'Orbitron', sans-serif" }, // 80s digital/sci-fi
  { before: 2000, family: "'Comic Neue', cursive" }, // 90s casual/grunge-adjacent
  { before: 2010, family: "'Quicksand', sans-serif" }, // 2000s rounded web
  { before: 2020, family: "'Montserrat', sans-serif" }, // 2010s geometric minimalist
];
const CURRENT_ERA_FONT = "'Inter', sans-serif";

function decadeFont(publishedYear) {
  if (!publishedYear) return CURRENT_ERA_FONT;
  const bucket = DECADE_FONTS.find(({ before }) => publishedYear < before);
  return bucket ? bucket.family : CURRENT_ERA_FONT;
}

// Two ways to draw the same shelf, chosen per account in display settings.
//
// UPRIGHT is the shelf a room has: books on their edges, titles running
// vertically, many to a row. STACKED turns them a quarter turn into flat
// piles, which fits far fewer books in the same width but lets every title
// read left to right - the better trade on a screen nobody walks up to, and
// so the default.
export const UPRIGHT = 'upright';
export const STACKED = 'stacked';

// How much shorter than its column a stacked book may be. A pile of identical
// lengths reads as a solid block; a little raggedness reads as books.
const MAX_SPINE_INSET = 40;

// Thickness of the spine: how wide it is standing up, how tall it is lying
// down. From page count either way, with a floor that keeps the title legible
// rather than matching the thinnest real book.
function spineThickness(book) {
  return Math.round(Math.min(48, Math.max(22, 18 + (book.pages || 220) / 16)));
}

// Height of an upright book, hashed so a row has the ragged top edge a real
// shelf does and keeps it across reloads.
function spineHeight(book) {
  return 170 + (hashString(bookSeed(book)) % 45);
}

// What the spine may be drawn at, whatever the cover turns out to be. A cover
// that is very nearly white or black is a real cover and should look like one,
// but a spine at the very ends of the range has no room left for a title, so
// the lightness is held just inside them.
const MIN_LIGHTNESS = 12;
const MAX_LIGHTNESS = 88;
// And a ceiling on saturation, because a cover can be more vivid than anything
// that belongs on a wall in a dim room.
const MAX_SATURATION = 70;
// The two inks a spine title is ever printed in. Which one is chosen per book
// by measured contrast, not by a lightness threshold - see titleInk.
const DARK_INK = [43, 38, 34];
const LIGHT_INK = [243, 234, 217];

// spineColor is what Classic draws a book in: the cover colour, held inside
// the range this theme can print a title on.
//
// Saturation and lightness come from the cover too, which is what lets a
// black, white or grey book look like one. They used to be fixed, so every
// spine came out the same muddy mid-tone and a monochrome cover was
// indistinguishable from a coloured one.
function spineColor(book) {
  const { hue, saturation, lightness, computed } = coverColour(book);
  if (!computed) return { hue, saturation, lightness };

  return {
    hue,
    saturation: clamp(saturation, 0, MAX_SATURATION),
    lightness: clamp(lightness, MIN_LIGHTNESS, MAX_LIGHTNESS),
  };
}

// readableSpine keeps the cover's hue and saturation - they are what makes a
// book recognisable on the shelf - and moves only its tone, and only as far as
// it takes for the title to be readable on it.
//
// The colors that need this are vivid mid-tones, where neither ink is far
// enough away. Everything else is returned untouched.
function readableSpine(book) {
  const base = spineColor(book);
  const ink = inkFor(base);

  let lightness = base.lightness;
  // Away from the ink: darken under light text, lighten under dark.
  const step = ink === LIGHT_INK ? -4 : 4;

  for (let i = 0; i < 20; i++) {
    const candidate = { ...base, lightness };
    if (contrast(hslToRgb(candidate.hue, candidate.saturation, candidate.lightness), ink) >= MIN_CONTRAST) break;

    const next = clamp(lightness + step, MIN_LIGHTNESS, MAX_LIGHTNESS);
    if (next === lightness) break; // At the end of what the display will draw.
    lightness = next;
  }

  return { ...base, lightness, ink: rgbCSS(ink) };
}

function spineStyle(book, layout) {
  const { hue, saturation, lightness } = readableSpine(book);
  // The gradient runs across the thickness in both layouts, so the lit edge
  // is the left of a standing book and the top of a lying one.
  const angle = layout === UPRIGHT ? '90deg' : '180deg';
  // A few points either side, so the spine still reads as a rounded object
  // rather than a flat rectangle, at whatever tone the cover put it.
  const shading = `linear-gradient(${angle}, hsl(${hue}, ${saturation}%, ${clamp(lightness - 4, 3, 96)}%), ` +
    `hsl(${hue}, ${saturation}%, ${clamp(lightness + 4, 3, 96)}%))`;

  if (layout === UPRIGHT) {
    return {
      width: `${spineThickness(book)}px`,
      height: `${spineHeight(book)}px`,
      background: shading,
    };
  }

  return {
    // Length comes from the column, not from a fixed size, so a wide shelf
    // spends the extra room on titles instead of on gaps.
    width: `calc(100% - ${hashString(bookSeed(book)) % MAX_SPINE_INSET}px)`,
    height: `${spineThickness(book)}px`,
    background: shading,
  };
}

function inkFor({ hue, saturation, lightness }) {
  return mostReadable(hslToRgb(hue, saturation, lightness), [DARK_INK, LIGHT_INK]).ink;
}

function titleInk(book) {
  return readableSpine(book).ink;
}

function BookSpine({ book, layout, shelfKey, onSelect }) {
  // The color comes from the scraper, which has already fetched and decoded
  // this cover once for the whole account (see EnrichSpineColors). A book
  // without one draws in a hue hashed from its title until a sync fills it in.
  //
  // The display used to decode covers itself as a fallback, with its own copy
  // of the bucketing and the neutral-pixel rules. That went when the color
  // grew from a hue into a hue, saturation and lightness: keeping two
  // implementations of "what color is this cover" in two languages would have
  // meant them drifting, and a television is the worst place to do the work.

  function handleClick(event) {
    event.stopPropagation();
    onSelect(shelfKey, book.id);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  }

  return (
    <div
      className="spine-wrap"
      // The book's id, for anything that has to find one book among many - a
      // title is not an identity, and this account's shelf has two books
      // called "Atmosphere".
      data-book={book.id}
      tabIndex={0}
      role="button"
      aria-label={`Highlight ${book.title}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="spine" style={spineStyle(book, layout)} title={book.title}>
        <span
          className="spine-title"
          style={{ fontFamily: decadeFont(book.publishedYear), color: titleInk(book) }}
        >
          {shortTitle(book.title)}
        </span>
      </div>
    </div>
  );
}

function ShelfRow({ shelfKey, books, layout, onSelect }) {
  function handleShelfKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(shelfKey, null);
    }
  }

  return (
    <section className="shelf-row">
      <div
        className="shelf-case"
        role="button"
        tabIndex={0}
        aria-label={`Highlight the ${shelfLabel(shelfKey)} shelf`}
        onClick={() => onSelect(shelfKey, null)}
        onKeyDown={handleShelfKeyDown}
      >
        <div className={`shelf-books shelf-books--${layout}`}>
          {books.map((book) => (
            <BookSpine
              key={book.id}
              book={book}
              layout={layout}
              shelfKey={shelfKey}
              onSelect={onSelect}
            />
          ))}
        </div>
        <div className="shelf-ledge">
          <span className="shelf-label">
            {shelfLabel(shelfKey)}
            <span className="shelf-count">{books.length}</span>
          </span>
        </div>
      </div>
    </section>
  );
}

export default function Bookshelf({ books, settings = {}, onSelect }) {
  const shelves = groupByShelf(books, settings);
  // Anything the display settings do not recognise draws as a stack rather
  // than as nothing, so an unknown value costs readability and not the shelf.
  const mode = settings.bookshelfLayout === UPRIGHT ? UPRIGHT : STACKED;

  return (
    <div className="bookshelf">
      {shelves.map(({ shelfKey, books: shelfBooks }) => (
        <ShelfRow
          key={shelfKey}
          shelfKey={shelfKey}
          books={shelfBooks}
          layout={mode}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
